import React from 'react';

import uniqueId from 'lodash/uniqueId';

import { classnames } from '@lumapps/classnames';
import { useDataAttributes } from '@lumapps/data-attributes';
import { SearchField, SearchFieldProps } from '@lumapps/lumx-filter-and-sort/components/SearchField';
import './index.scss';
/* eslint-disable import/order */
import { EmptyState } from '@lumapps/lumx-states/components/EmptyState';
import { NoResultsState } from '@lumapps/lumx-states/components/NoResultsState';
import { ServiceNotAvailableState } from '@lumapps/lumx-states/components/ServiceNotAvailableState';
import { WarningState } from '@lumapps/lumx-states/components/WarningState';
import { mdiImageBrokenVariant, mdiClose, mdiCloseCircle } from '@lumapps/lumx/icons';
import {
    Button,
    ButtonProps,
    Dialog,
    DialogProps,
    Emphasis,
    FlexBox,
    IconButton,
    Orientation,
    Size,
    Toolbar,
    AlertDialog,
    Text,
} from '@lumapps/lumx/react';
import { GLOBAL, useTranslate } from '@lumapps/translations';
import { useInfiniteScroll } from '@lumapps/utils/hooks/useInfiniteScroll';
import { PickerList } from '../PickerList';
/* eslint-enable import/order */

export const CLASSNAME = 'picker-dialog';

export interface PickerDialogProps<I> {
    /** Picker list loading state. */
    isListLoading?: boolean;
    /** ReactNode to display at the end of the picker list. */
    afterList?: React.ReactNode;
    /** Picker dialog title. */
    title: string;
    /** Items to pick (must have an id). */
    items?: Array<I>;
    /** Items already selected on picker init */
    initSelectedItems?: I[];
    /** Item key selector. */
    itemKeySelector(item: I): string | number;
    /** Item rendered. */
    itemRenderer: React.FC<I>;
    /** Whether the multiple selection is allowed */
    isMultipleSelect?: boolean;
    /** Search text. */
    searchValue?: string;
    /** Search text placeholder. */
    searchPlaceholder?: string;
    /** Lokalise key for "N selected items" label to go in the clear button (if multiple select is enabled) */
    selectedItemsLabelKey?: string;
    /** Override the picker dialog content. */
    children?: React.ReactNode;
    /** CSS class name. */
    className?: string;
    /** Additional filter fields to add to the right of the search field. */
    additionalFilterFields?: React.ReactNode;
    /** Whether the dialog is opened or not. */
    isOpen?: DialogProps['isOpen'];
    /** Dialog size. */
    size?: DialogProps['size'];
    /** Dialog loading state. */
    isLoading?: DialogProps['isLoading'];
    /** callback when dialog visibility has changed */
    onVisibilityChange?: DialogProps['onVisibilityChange'];
    /** Dialog parent element (triggers focus on close). */
    parentElement?: DialogProps['parentElement'];
    /** Cancel button props. */
    cancelButtonProps?: ButtonProps;
    /** Confirm button props. */
    confirmButtonProps?: ButtonProps;
    /** additional props for the search field component */
    searchFieldProps?: Partial<SearchFieldProps>;
    /** Reset list selection and keyboard nav on items change. */
    resetListOnItemsChange?: boolean;
    /** Should we displayed an alert modal when the user click away */
    shouldPreventClose?: boolean;
    /** Is there an error */
    hasError?: boolean;
    /** should the picker display an empty state instead of a no result */
    shouldDisplayEmptyState?: boolean;
    /** Label displayed when in empty state (no result with no filter if it is enabled) */
    emptyStateLabel?: string;
    /** callback when clear button is clicked on no results with filter applied (query or filters) */
    onNoResultsClear?(): void;
    /** On picker list scroll end. */
    onScrollEnd?(): void;
    /** On selection canceled. */
    onSelectionCancel?(): void;
    /** Search text change. */
    onSearchChange?(newValue: string): void;
    /** On selection confirmed. */
    onSelectionConfirm(items: I[]): void;
    /** Reloading callback in cas of error */
    onReload?(): void;
}

/**
 * Generic dialog for displaying a list of entities from which to pick from.
 *
 * @family Dialog
 * @param PickerDialogProps
 * @returns PickerDialog
 */
export const PickerDialog = <I,>(props: PickerDialogProps<I>) => {
    const {
        additionalFilterFields,
        afterList,
        cancelButtonProps,
        className,
        confirmButtonProps,
        isOpen,
        isListLoading,
        isLoading,
        shouldDisplayEmptyState,
        initSelectedItems,
        items,
        itemKeySelector,
        itemRenderer,
        onNoResultsClear,
        onSelectionCancel,
        onSelectionConfirm,
        onScrollEnd,
        onSearchChange,
        parentElement,
        searchPlaceholder,
        searchValue,
        size,
        title,
        resetListOnItemsChange,
        shouldPreventClose = true,
        hasError = false,
        isMultipleSelect = false,
        selectedItemsLabelKey,
        emptyStateLabel,
        searchFieldProps = {},
        onReload,
        ...rest
    } = props;
    const { translateKey, translateAndReplace, pluralize } = useTranslate();
    const clearSearch = React.useCallback(() => onSearchChange?.(''), [onSearchChange]);
    const [selectedItems, setSelectedItems] = React.useState<I[]>([]);
    const { get } = useDataAttributes('picker-dialog');
    const [isAlertDialogOpen, setAlertDialogOpen] = React.useState(false);

    const confirmDialogId = React.useMemo(() => uniqueId(`${CLASSNAME}-confirm-dialog-`), []);
    const closeAlertDialog = React.useCallback(() => setAlertDialogOpen(false), [setAlertDialogOpen]);
    const openAlertDialog = React.useCallback(() => setAlertDialogOpen(!!isOpen), [setAlertDialogOpen, isOpen]);
    const handleCancel = React.useCallback(() => {
        setSelectedItems([]);
        clearSearch();
        closeAlertDialog();
        if (onSelectionCancel) {
            onSelectionCancel();
        }
    }, [clearSearch, closeAlertDialog, onSelectionCancel]);

    const handleConfirm = React.useCallback(() => {
        setSelectedItems([]);
        clearSearch();
        closeAlertDialog();
        onSelectionConfirm(selectedItems);
    }, [clearSearch, closeAlertDialog, onSelectionConfirm, selectedItems]);

    const onDialogClose = () => {
        if (shouldPreventClose) {
            openAlertDialog();
        } else {
            handleCancel();
        }
    };

    const contentRef = React.useRef(null);
    useInfiniteScroll(contentRef, !items?.length || isListLoading ? undefined : onScrollEnd);

    const searchInputRef = React.useRef(null);
    const labelId = React.useMemo(() => uniqueId(`${CLASSNAME}-label-`), []);

    const selectedItemsLabel = React.useMemo(
        () =>
            selectedItemsLabelKey
                ? translateAndReplace(pluralize(selectedItemsLabelKey, selectedItems.length), {
                      COUNT: selectedItems.length,
                      NB: selectedItems.length,
                  })
                : null,
        [pluralize, selectedItems.length, selectedItemsLabelKey, translateAndReplace],
    );

    // Key used to force reset of the picker list component tree.
    const listKey = React.useRef('');
    React.useEffect(() => {
        if (items && resetListOnItemsChange) {
            setSelectedItems([]);
            // Generating a new key.
            listKey.current = resetListOnItemsChange ? uniqueId() : '';
        }
    }, [items, resetListOnItemsChange]);

    const hasNoResult = (!items || items.length === 0) && !isListLoading && !hasError;

    const isEmpty = hasNoResult && shouldDisplayEmptyState && emptyStateLabel;

    return (
        <>
            <Dialog
                className={classnames(className, CLASSNAME)}
                dialogProps={{ 'aria-labelledby': labelId }}
                forceHeaderDivider
                parentElement={parentElement}
                focusElement={searchInputRef}
                contentRef={contentRef}
                isOpen={isOpen}
                isLoading={isLoading}
                size={size}
                onClose={onDialogClose}
                {...rest}
            >
                <header>
                    <Toolbar
                        label={
                            <h1 id={labelId} className="lumx-typography-title">
                                {title}
                            </h1>
                        }
                        after={
                            <IconButton
                                emphasis={Emphasis.low}
                                icon={mdiClose}
                                onClick={handleCancel}
                                label={translateKey(GLOBAL.CLOSE)}
                            />
                        }
                    />
                    <FlexBox
                        orientation={Orientation.horizontal}
                        gap={Size.regular}
                        className="lumx-spacing-padding-horizontal-huge"
                    >
                        <SearchField
                            label={searchPlaceholder || translateKey(GLOBAL.SEARCH)}
                            value={searchValue}
                            onChange={onSearchChange as any}
                            onSearch={onSearchChange as any}
                            isCompact
                            debounced
                            textFieldProps={{
                                inputRef: searchInputRef,
                                placeholder: searchPlaceholder,
                                'aria-label': searchPlaceholder,
                                isDisabled: Boolean(isEmpty),
                                ...get({ element: 'search-input' }),
                            }}
                            wrapperProps={{
                                className: `${CLASSNAME}__search-field`,
                            }}
                            {...searchFieldProps}
                        />
                        {additionalFilterFields}
                    </FlexBox>
                </header>

                {Boolean(hasError && onReload) && (
                    <ServiceNotAvailableState onRetry={onReload} className="lumx-spacing-margin-vertical-huge" />
                )}
                {Boolean(hasError && !onReload) && (
                    <WarningState
                        icon={mdiImageBrokenVariant}
                        title={translateKey(GLOBAL.GENERIC_ERROR)}
                        className="lumx-spacing-margin-vertical-huge"
                    />
                )}
                {Boolean(hasNoResult && !shouldDisplayEmptyState && !onNoResultsClear) && (
                    <EmptyState
                        title={translateKey(GLOBAL.NO_RESULTS_FOUND)}
                        message={translateKey(GLOBAL.TRY_ANOTHER_QUERY_OR_FILTER)}
                        isVerticallyCentered={false}
                    />
                )}
                {Boolean(hasNoResult && !shouldDisplayEmptyState && onNoResultsClear) && (
                    <NoResultsState
                        searchValue={searchValue}
                        emptyWithFiltersText={emptyStateLabel}
                        onClear={onNoResultsClear}
                    />
                )}
                {isEmpty && (
                    <EmptyState message={emptyStateLabel} messageProps={{ as: 'span' }} isVerticallyCentered={false} />
                )}
                {Boolean(!hasError && ((items && items.length) || isListLoading)) && (
                    <PickerList<I>
                        key={listKey.current}
                        aria-labelledby={labelId}
                        items={items as I[]}
                        itemRenderer={itemRenderer as any}
                        itemKeySelector={itemKeySelector}
                        afterList={afterList}
                        alreadyUsedItems={initSelectedItems}
                        disabledItems={initSelectedItems}
                        {...(isMultipleSelect
                            ? {
                                  isMultiple: true,
                                  selectedItems: selectedItems as I[],
                                  setSelectedItems: setSelectedItems as (items: I[]) => void,
                              }
                            : {
                                  selectedItem: selectedItems[0] as I,
                                  setSelectedItem: (item: I): void => setSelectedItems([item]),
                              })}
                    />
                )}
                <footer>
                    <Toolbar
                        label={
                            Boolean(selectedItemsLabel && isMultipleSelect && selectedItems.length) && (
                                <FlexBox orientation={Orientation.horizontal} gap="regular" hAlign="center">
                                    <p className="lumx-typography-subtitle1">{selectedItemsLabel}</p>
                                    <Button
                                        emphasis={Emphasis.low}
                                        onClick={() => setSelectedItems([])}
                                        rightIcon={mdiCloseCircle}
                                    >
                                        {translateKey(GLOBAL.CLEAR)}
                                    </Button>
                                </FlexBox>
                            )
                        }
                        after={
                            <FlexBox orientation={Orientation.horizontal} gap="regular">
                                <Button
                                    emphasis={Emphasis.medium}
                                    {...cancelButtonProps}
                                    className={classnames(confirmButtonProps?.className, `${CLASSNAME}__cancel-button`)}
                                    onClick={handleCancel}
                                >
                                    {cancelButtonProps?.children || translateKey(GLOBAL.CANCEL)}
                                </Button>
                                <Button
                                    {...confirmButtonProps}
                                    className={classnames(
                                        confirmButtonProps?.className,
                                        `${CLASSNAME}__confirm-button`,
                                    )}
                                    onClick={handleConfirm}
                                    isDisabled={!selectedItems.length}
                                    tabIndex={!selectedItems.length ? -1 : 0}
                                >
                                    {confirmButtonProps?.children || translateKey(GLOBAL.OK)}
                                </Button>
                            </FlexBox>
                        }
                    />
                </footer>
            </Dialog>
            <AlertDialog
                id={confirmDialogId}
                title={translateKey(GLOBAL.CONFIRM_CLOSE)}
                kind="warning"
                isOpen={isOpen && isAlertDialogOpen}
                onClose={closeAlertDialog}
                cancelProps={{ onClick: closeAlertDialog, label: translateKey(GLOBAL.CANCEL) }}
                confirmProps={{ onClick: handleCancel, label: translateKey(GLOBAL.OK) }}
            >
                <Text as="p" typography="body1">
                    {translateKey(GLOBAL.CONFIRM_CLOSE_DESCRIPTION)}
                </Text>
            </AlertDialog>
        </>
    );
};
PickerDialog.defaultProps = {
    size: Size.regular,
};
